import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react';
import clsx from 'clsx';
import { ChevronDownIcon } from 'lucide-react';

import React, { useMemo, useRef, useState } from 'react';

import {
  CURRENCIES,
  getCurrencySymbol,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';

import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';

import { getInputClasses } from '@/lib/styles/form';

interface SearchableCurrencySelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  mode?: 'group' | 'expense';
  placeholder?: string;
  'data-testid'?: string;
}

export function SearchableCurrencySelect({
  id,
  name,
  value,
  onChange,
  className = '',
  disabled = false,
  required = false,
  mode = 'expense',
  placeholder,
  'data-testid': testId,
}: SearchableCurrencySelectProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load required namespaces
  const groupsLoaded = useNamespace('groups');
  const currenciesLoaded = useNamespace('currencies');

  // Build currency options
  const allCurrencies = useMemo(() => {
    // Don't build the list until translations are loaded
    if (!currenciesLoaded) return [];

    const currencies = Object.values(CURRENCIES).map(currency => ({
      value: currency.code,
      code: currency.code,
      name: t(`currencies:${currency.code}`),
      symbol: getCurrencySymbol(currency.code),
      searchText:
        `${currency.code} ${t(`currencies:${currency.code}`)}`.toLowerCase(),
    }));

    // Sort by translated name
    currencies.sort((a, b) => a.name.localeCompare(b.name));

    // Add "none" option for group mode (only if translations are loaded)
    if (mode === 'group' && groupsLoaded) {
      currencies.unshift({
        value: '',
        code: '',
        name: t('groups:fields.defaultCurrencyNone'),
        symbol: '',
        searchText: t('groups:fields.defaultCurrencyNone').toLowerCase(),
      });
    }

    return currencies;
  }, [t, mode, groupsLoaded, currenciesLoaded]);

  // Filter currencies based on search query
  const filteredCurrencies = useMemo(() => {
    if (query === '') return allCurrencies;

    const searchQuery = query.toLowerCase();
    return allCurrencies.filter(currency =>
      currency.searchText.includes(searchQuery)
    );
  }, [query, allCurrencies]);

  // Get display value for selected currency
  const selectedCurrency = allCurrencies.find(c => c.value === value);
  const defaultDisplayValue = selectedCurrency
    ? selectedCurrency.code
      ? `${selectedCurrency.code} - ${selectedCurrency.name}`
      : selectedCurrency.name
    : '';

  // When searching, show query if it exists, otherwise show the current value
  const displayValue =
    isSearching && query !== '' ? query : defaultDisplayValue;

  const handleChange = (newValue: string | null) => {
    if (newValue !== null) {
      onChange(newValue);
    }
    // Blur the input after selection
    setTimeout(() => {
      const input = document.getElementById(id!);
      if (input) {
        input.blur();
      }

      // Focus the next logical input based on mode
      if (mode === 'expense') {
        // For expenses/settlements, focus amount input only if it's empty
        const amountInput = document.getElementById(
          'amount'
        ) as HTMLInputElement;
        if (amountInput && (!amountInput.value || amountInput.value === '0')) {
          amountInput.focus();
        }
      } else if (mode === 'group') {
        // For groups, always focus the budget input after currency selection
        const budgetInput = document.getElementById(
          'budget'
        ) as HTMLInputElement;
        if (budgetInput) {
          budgetInput.focus();
        }
      }
    }, 0);
  };

  // Show a loading state if translations aren't ready
  if (!currenciesLoaded) {
    return (
      <div
        className={clsx(
          getInputClasses({ disabled: true }),
          'animate-pulse',
          className
        )}
      />
    );
  }

  return (
    <Combobox value={value} onChange={handleChange} disabled={disabled}>
      <div className="relative">
        <div className="relative">
          <ComboboxInput
            ref={inputRef}
            id={id}
            name={name}
            required={required}
            data-testid={testId}
            autoComplete="off"
            displayValue={() => displayValue}
            onChange={_event => {
              setQuery(_event.target.value);
              setIsSearching(true);
            }}
            onFocus={_event => {
              setIsSearching(true);
              // Select all text when focusing for better UX - use setTimeout to ensure it works with Headless UI
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.select();
                }
              }, 0);
            }}
            onBlur={() => {
              setIsSearching(false);
              setQuery('');
            }}
            className={clsx(getInputClasses({ disabled }), 'pr-10', className)}
            placeholder={
              placeholder ||
              (mode === 'expense'
                ? t('expenses:placeholders.selectCurrency')
                : undefined)
            }
          />
          <ComboboxButton className="absolute inset-0 w-full cursor-pointer">
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
          </ComboboxButton>
        </div>

        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setQuery('');
            setIsSearching(false);
          }}
        >
          <ComboboxOptions className="absolute z-10 mt-2 max-h-60 w-full sm:w-max sm:min-w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-700 focus:outline-hidden sm:text-sm">
            {filteredCurrencies.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                {t('common:labels.noResults')}
              </div>
            ) : (
              filteredCurrencies.map(currency => (
                <ComboboxOption
                  key={currency.value}
                  value={currency.value}
                  className={({ active, selected }) =>
                    clsx(
                      'relative cursor-default select-none flex items-center pl-2 pr-4 py-2 transition-colors sm:whitespace-nowrap',
                      'text-gray-900 dark:text-gray-100',
                      active
                        ? 'bg-[#F6F5F3] dark:bg-gray-700/50'
                        : selected
                          ? ''
                          : ''
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      {selected ? (
                        <CheckmarkIcon
                          className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mr-1.5"
                          aria-hidden="true"
                        />
                      ) : (
                        <div className="w-4 h-4 mr-1.5" />
                      )}
                      <span
                        className={clsx(
                          'block truncate',
                          selected ? 'font-medium' : 'font-normal'
                        )}
                      >
                        {currency.code ? (
                          <>
                            <span className="font-medium">{currency.code}</span>
                            <span className="ml-2 text-sm opacity-75">
                              {currency.symbol} - {currency.name}
                            </span>
                          </>
                        ) : (
                          currency.name
                        )}
                      </span>
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
}

export default SearchableCurrencySelect;
